import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Helmet } from 'react-helmet';
import './RegisterForm.css';

const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const location = useLocation();

  const getCodigoReferidoFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('key_referido');
  };

  const generateUniqueReferralCode = async () => {
    let isUnique = false;
    let referralCode = '';
    let attempts = 0;

    while (!isUnique && attempts < 10) {
      attempts++;
      referralCode = Math.random().toString(36).substring(2, 10).toUpperCase();
      try {
        const { data } = await supabase
          .from('usuarios')
          .select('codigo_referido')
          .eq('codigo_referido', referralCode);

        if (!data || data.length === 0) {
          isUnique = true;
        }
      } catch (err) {
        isUnique = true;
      }
    }

    return referralCode || Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const checkAndCreateUser = async (user) => {
    if (!user) return null;

    try {
      const { data: existingUser } = await supabase
        .from('usuarios')
        .select('id, codigo_referido')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!existingUser) {
        const referralCode = await generateUniqueReferralCode();
        await supabase
          .from('usuarios')
          .insert({
            user_id: user.id,
            codigo_referido: referralCode,
            email: user.email,
            role: 'usuario',
          });

        return referralCode;
      }
      return existingUser.codigo_referido;
    } catch (err) {
      console.warn('Nota en checkAndCreateUser:', err);
      return null;
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!email || !password) {
      setError('Por favor completa todos los campos.');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');
    setIsSuccess(false);

    const codigoReferido = getCodigoReferidoFromUrl();

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (signUpError) {
        setError(signUpError.message || 'Error al registrar. Por favor, intenta de nuevo.');
        setIsSubmitting(false);
        return;
      }

      const user = data?.user;

      if (user) {
        const referralCode = await checkAndCreateUser(user);

        if (codigoReferido && referralCode) {
          try {
            await supabase.from('referidos').insert({
              referente_codigo: codigoReferido,
              referido_codigo: referralCode,
            });
          } catch (refErr) {
            console.warn('Error al vincular referido:', refErr);
          }
        }
      }

      setIsSuccess(true);
      setMessage(
        '¡Registro exitoso! Te hemos enviado un correo de confirmación. Revisa tu bandeja de entrada o spam para activar tu cuenta.'
      );
    } catch (err) {
      console.error('Error durante el registro:', err);
      setError('Error al registrar. Por favor, intenta de nuevo más tarde.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20 relative bg-dark-grid">
      <Helmet>
        <title>Regístrate - MatiMarket</title>
        <meta
          name="description"
          content="Regístrate en MatiMarket y descubre productos innovadores, tiendas oficiales y premios exclusivos."
        />
      </Helmet>

      {/* Ambient background glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10 glass-card p-8 rounded-2xl border border-gold/30 shadow-2xl backdrop-blur-xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/10 border border-gold/40 mb-3 text-gold shadow-lg shadow-gold/20">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </div>
          <h1 className="text-2xl font-black text-white uppercase tracking-wider">
            Únete a <span className="text-gold-gradient">MatiMarket</span>
          </h1>
          <p className="text-sm text-gray-300 mt-1">Crea tu cuenta y empieza a acumular puntos</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 text-sm flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {isSuccess ? (
          <div className="p-6 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/30 flex items-center justify-center text-emerald-300">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">¡Cuenta Creada!</h3>
            <p className="text-sm text-emerald-200 mb-6">{message}</p>
            <Link
              to="/ingresar"
              className="inline-block w-full py-3 px-4 rounded-xl font-bold uppercase tracking-wider text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 transition"
            >
              Ir a Iniciar Sesión
            </Link>
          </div>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label htmlFor="reg-email" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                Correo Electrónico
              </label>
              <input
                id="reg-email"
                type="email"
                required
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
              />
            </div>

            <div>
              <label htmlFor="reg-password" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                Contraseña
              </label>
              <input
                id="reg-password"
                type="password"
                required
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
              />
            </div>

            <div>
              <label htmlFor="reg-confirm" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                Confirmar Contraseña
              </label>
              <input
                id="reg-confirm"
                type="password"
                required
                placeholder="Repite la contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 px-4 rounded-xl font-bold uppercase tracking-wider text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 shadow-lg shadow-gold/25 hover:shadow-gold/40 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 mt-6"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  <span>Registrando...</span>
                </>
              ) : (
                'Crear Mi Cuenta'
              )}
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-gray-800 text-center text-sm text-gray-400">
          ¿Ya tienes una cuenta?{' '}
          <Link to="/ingresar" className="text-gold font-bold hover:underline">
            Inicia sesión
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
